import AppIcon from '../components/AppIcon';
import React, { useState, useEffect } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { useAuth } from '../hooks/useAuth';
import { GET_MEMBER, CREATE_MEMBER, UPDATE_MEMBER } from '../services/members';
import authStorage from '../store/authStorage';

const TABS = [
  { key: 'account', label: 'Account', icon: 'overview' },
  { key: 'family', label: 'Family', icon: 'family' },
  { key: 'appearance', label: 'Appearance', icon: 'adjective' },
];

function SettingsPage() {
  const [activeTab, setActiveTab] = useState('account');
  const { user } = useAuth();

  // Derive personId from user or localStorage
  const [personId, setPersonId] = useState(null);
  useEffect(() => {
    const stored = localStorage.getItem('mien_person_id');
    if (stored) {
      setPersonId(stored);
    } else if (user?.personId) {
      setPersonId(String(user.personId));
      localStorage.setItem('mien_person_id', String(user.personId));
    }
  }, [user]);

  // Family settings (local for now)
  const [familySettings, setFamilySettings] = useState({ name: 'Saetern Family', description: 'Preserving our Mien heritage', defaultPermission: 'VIEWER', expirationDays: '7' });
  const [theme, setTheme] = useState('light');
  const [language, setLanguage] = useState('en');
  const [dateFormat, setDateFormat] = useState('MM/DD/YYYY');
  const [timeFormat, setTimeFormat] = useState('12');
  const [firstDayOfWeek, setFirstDayOfWeek] = useState('sunday');
  const [defaultTreeView, setDefaultTreeView] = useState('descendant');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [saving, setSaving] = useState(false);

  const handleSave = (section) => {
    setSaving(true);
    setTimeout(() => setSaving(false), 1000);
  };

  return (
    <div className="">
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="border-b border-gray-200 mb-6">
          <nav className="flex gap-1 -mb-px overflow-x-auto">
            {TABS.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 whitespace-nowrap transition-colors ${
                  activeTab === tab.key ? 'border-indigo-600 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                <AppIcon name={tab.icon} size={16} /> {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {activeTab === 'account' && <AccountTab personId={personId} setPersonId={setPersonId} user={user} />}
        {activeTab === 'family' && <FamilyTab settings={familySettings} setSettings={setFamilySettings} onSave={handleSave} saving={saving} />}
        {activeTab === 'appearance' && <AppearanceTab theme={theme} setTheme={setTheme} language={language} setLanguage={setLanguage} dateFormat={dateFormat} setDateFormat={setDateFormat} timeFormat={timeFormat} setTimeFormat={setTimeFormat} firstDayOfWeek={firstDayOfWeek} setFirstDayOfWeek={setFirstDayOfWeek} defaultTreeView={defaultTreeView} setDefaultTreeView={setDefaultTreeView} showResetConfirm={showResetConfirm} setShowResetConfirm={setShowResetConfirm} onSave={handleSave} saving={saving} />}
      </main>
    </div>
  );
}

function AccountTab({ personId, setPersonId, user }) {
  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    spiritualName: '',
    adultName: '',
    gender: 'male',
    birthDate: '',
    birthPlace: '',
    isLiving: true,
  });
  const [passwords, setPasswords] = useState({ current: '', new: '', confirm: '' });
  const [notifications, setNotifications] = useState({ memories: true, invitations: true, events: true, kinship: false });
  const [privacy, setPrivacy] = useState({ birthDate: true, contactInfo: false, location: false });
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [saveStatus, setSaveStatus] = useState(null); // 'saving' | 'saved' | 'error' | null

  // Fetch existing member data if personId exists
  const { data: memberData, loading: memberLoading, refetch } = useQuery(GET_MEMBER, {
    variables: { id: personId },
    skip: !personId,
  });

  const [createMember, { loading: creating }] = useMutation(CREATE_MEMBER);
  const [updateMember, { loading: updating }] = useMutation(UPDATE_MEMBER);

  const member = memberData?.member;
  const saving = creating || updating;

  // Populate form when member data loads
  useEffect(() => {
    if (member) {
      setForm({
        firstName: member.firstName || '',
        lastName: member.lastName || '',
        spiritualName: member.spiritualName || '',
        adultName: member.adultName || '',
        gender: member.gender || 'male',
        birthDate: member.birthDate || '',
        birthPlace: member.birthPlace || '',
        isLiving: member.isLiving !== false,
      });
    }
  }, [member]);

  const handleSaveProfile = async () => {
    setSaveStatus('saving');
    try {
      const input = {
        firstName: form.firstName,
        lastName: form.lastName || null,
        spiritualName: form.spiritualName || null,
        adultName: form.adultName || null,
        gender: form.gender,
        birthDate: form.birthDate || null,
        birthPlace: form.birthPlace || null,
        isLiving: form.isLiving,
      };

      if (personId) {
        // Update existing member
        const result = await updateMember({ variables: { id: personId, input } });
        setSaveStatus('saved');
      } else {
        // Create new member (first-time profile setup)
        const result = await createMember({ variables: { input } });
        const newId = result.data?.createMember?.id;
        if (newId) {
          setPersonId(String(newId));
          localStorage.setItem('mien_person_id', String(newId));
          refetch({ id: String(newId) });
        }
        setSaveStatus('saved');
      }
    } catch (err) {
      console.error('Failed to save profile:', err);
      setSaveStatus('error');
    }
    setTimeout(() => setSaveStatus(null), 3000);
  };

  return (
    <div className="space-y-6">
      {/* Profile Setup Banner for new users */}
      {!personId && (
        <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-4">
          <div className="flex items-start gap-3">
            <AppIcon name="overview" size={20} />
            <div>
              <h3 className="text-sm font-semibold text-indigo-800">Complete Your Profile</h3>
              <p className="text-sm text-indigo-600 mt-1">Fill in your personal details to get started with the family tree. This creates your person record in the family.</p>
            </div>
          </div>
        </div>
      )}

      {/* Profile */}
      <section className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          {personId ? 'Personal Information' : 'Create Your Profile'}
        </h3>
        {memberLoading ? (
          <div className="text-center py-8 text-gray-500">Loading profile...</div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">First Name *</label>
                <input type="text" value={form.firstName} onChange={(e) => setForm({...form, firstName: e.target.value})} placeholder="Enter your first name" className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Last Name</label>
                <input type="text" value={form.lastName} onChange={(e) => setForm({...form, lastName: e.target.value})} placeholder="Enter your last name" className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Spiritual Name (Mien)</label>
                <input type="text" value={form.spiritualName} onChange={(e) => setForm({...form, spiritualName: e.target.value})} placeholder="e.g., Zeiv Mun" className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Adult Name (Mien)</label>
                <input type="text" value={form.adultName} onChange={(e) => setForm({...form, adultName: e.target.value})} placeholder="e.g., Zaangh Mbing" className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Gender *</label>
                <select value={form.gender} onChange={(e) => setForm({...form, gender: e.target.value})} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500">
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Date of Birth</label>
                <input type="date" value={form.birthDate} onChange={(e) => setForm({...form, birthDate: e.target.value})} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" />
              </div>
              <div className="sm:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Place of Birth</label>
                <input type="text" value={form.birthPlace} onChange={(e) => setForm({...form, birthPlace: e.target.value})} placeholder="e.g., Sacramento, CA" className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" />
              </div>
            </div>
            <div className="mt-4 flex items-center gap-3">
              <button onClick={handleSaveProfile} disabled={saving || !form.firstName} className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 disabled:opacity-50 transition-colors">
                {saveStatus === 'saving' ? 'Saving...' : personId ? 'Save Changes' : 'Create Profile'}
              </button>
              {saveStatus === 'saved' && (
                <span className="text-sm text-green-600 flex items-center gap-1">
                  <AppIcon name="check" size={14} /> Saved!
                </span>
              )}
              {saveStatus === 'error' && (
                <span className="text-sm text-red-600">Failed to save. Please try again.</span>
              )}
            </div>
          </>
        )}
      </section>

      {/* Account Info (read-only from auth) */}
      <section className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Account</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
            <input type="email" value={user?.email || ''} disabled className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-gray-50 text-gray-500" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
            <input type="text" value={user?.role || ''} disabled className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-gray-50 text-gray-500" />
          </div>
        </div>
      </section>

      {/* Change Password */}
      <section className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Change Password</h3>
        <div className="space-y-4 max-w-md">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Current Password</label>
            <input type="password" value={passwords.current} onChange={(e) => setPasswords({...passwords, current: e.target.value})} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">New Password</label>
            <input type="password" value={passwords.new} onChange={(e) => setPasswords({...passwords, new: e.target.value})} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Confirm New Password</label>
            <input type="password" value={passwords.confirm} onChange={(e) => setPasswords({...passwords, confirm: e.target.value})} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" />
          </div>
          <button className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 transition-colors">Update Password</button>
        </div>
      </section>

      {/* Notifications */}
      <section className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Notification Preferences</h3>
        <div className="space-y-3">
          {Object.entries(notifications).map(([key, val]) => (
            <label key={key} className="flex items-center justify-between py-2">
              <span className="text-sm text-gray-700 capitalize">{key === 'kinship' ? 'Kinship updates' : key}</span>
              <input type="checkbox" checked={val} onChange={(e) => setNotifications({...notifications, [key]: e.target.checked})} className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500" />
            </label>
          ))}
        </div>
      </section>

      {/* Privacy */}
      <section className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Privacy Settings</h3>
        <p className="text-sm text-gray-500 mb-4">Control what others can see on your profile.</p>
        <div className="space-y-3">
          {Object.entries(privacy).map(([key, val]) => (
            <label key={key} className="flex items-center justify-between py-2">
              <span className="text-sm text-gray-700">{key === 'birthDate' ? 'Show birth date' : key === 'contactInfo' ? 'Show contact info' : 'Show location'}</span>
              <input type="checkbox" checked={val} onChange={(e) => setPrivacy({...privacy, [key]: e.target.checked})} className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500" />
            </label>
          ))}
        </div>
      </section>

      {/* Danger Zone */}
      <section className="bg-white rounded-xl shadow-sm border border-red-200 p-6">
        <h3 className="text-lg font-semibold text-red-700 mb-2">Danger Zone</h3>
        <p className="text-sm text-gray-500 mb-4">This action cannot be undone.</p>
        {showDeleteConfirm ? (
          <div className="bg-red-50 p-4 rounded-lg">
            <p className="text-sm text-red-700 font-medium">Are you sure you want to delete your account?</p>
            <div className="mt-3 flex gap-3">
              <button className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 transition-colors">Yes, Delete Account</button>
              <button onClick={() => setShowDeleteConfirm(false)} className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors">Cancel</button>
            </div>
          </div>
        ) : (
          <button onClick={() => setShowDeleteConfirm(true)} className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 transition-colors">Delete Account</button>
        )}
      </section>
    </div>
  );
}

function FamilyTab({ settings, setSettings, onSave, saving }) {
  return (
    <div className="space-y-6">
      <section className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Family Settings</h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Family Name</label>
            <input type="text" value={settings.name} onChange={(e) => setSettings({...settings, name: e.target.value})} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea value={settings.description} onChange={(e) => setSettings({...settings, description: e.target.value})} rows={3} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 resize-y" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Default Permission for New Members</label>
            <select value={settings.defaultPermission} onChange={(e) => setSettings({...settings, defaultPermission: e.target.value})} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500">
              <option value="VIEWER">Viewer</option>
              <option value="CONTRIBUTOR">Contributor</option>
              <option value="ADMIN">Admin</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Invitation Expiration (days)</label>
            <input type="number" value={settings.expirationDays} onChange={(e) => setSettings({...settings, expirationDays: e.target.value})} min="1" max="30" className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" />
          </div>
        </div>
        <div className="mt-4 flex gap-3">
          <button onClick={() => onSave('family')} disabled={saving} className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 disabled:opacity-50 transition-colors">{saving ? 'Saving...' : 'Save Changes'}</button>
          <button className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors">Cancel</button>
        </div>
      </section>

      <section className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Export Data</h3>
        <p className="text-sm text-gray-500 mb-4">Download your family data in different formats.</p>
        <div className="flex flex-wrap gap-3">
          <button className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors"><AppIcon name="export" size={14} /> Export JSON</button>
          <button className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors"><AppIcon name="export" size={14} /> Export CSV</button>
        </div>
      </section>
    </div>
  );
}

function AppearanceTab({ theme, setTheme, language, setLanguage, dateFormat, setDateFormat, timeFormat, setTimeFormat, firstDayOfWeek, setFirstDayOfWeek, defaultTreeView, setDefaultTreeView, showResetConfirm, setShowResetConfirm, onSave, saving }) {
  const themes = [
    { key: 'light', label: 'Light', icon: 'light' },
    { key: 'dark', label: 'Dark', icon: 'dark' },
    { key: 'auto', label: 'Auto', icon: 'auto' },
  ];

  return (
    <div className="space-y-6">
      <section className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Theme</h3>
        <div className="grid grid-cols-3 gap-3">
          {themes.map((t) => (
            <button key={t.key} onClick={() => setTheme(t.key)} className={`flex flex-col items-center gap-2 p-4 rounded-xl border-2 transition-all ${theme === t.key ? 'border-indigo-500 bg-indigo-50' : 'border-gray-200 hover:border-gray-300'}`}>
              <AppIcon name={t.icon} size={24} />
              <span className="text-sm font-medium">{t.label}</span>
            </button>
          ))}
        </div>
      </section>

      <section className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Locale</h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Language</label>
            <select value={language} onChange={(e) => setLanguage(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500">
              <option value="en">English</option>
              <option value="mien">Mien (Iu Mienh)</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Date Format</label>
            <select value={dateFormat} onChange={(e) => setDateFormat(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500">
              <option value="MM/DD/YYYY">MM/DD/YYYY</option>
              <option value="DD/MM/YYYY">DD/MM/YYYY</option>
              <option value="YYYY-MM-DD">YYYY-MM-DD</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Time Format</label>
            <select value={timeFormat} onChange={(e) => setTimeFormat(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500">
              <option value="12">12-hour</option>
              <option value="24">24-hour</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">First Day of Week</label>
            <select value={firstDayOfWeek} onChange={(e) => setFirstDayOfWeek(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500">
              <option value="sunday">Sunday</option>
              <option value="monday">Monday</option>
            </select>
          </div>
        </div>
      </section>

      <section className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Default Tree View</h3>
        <div className="grid grid-cols-3 gap-3">
          {[
            { key: 'descendant', label: 'Descendant', icon: 'descendant' },
            { key: 'pedigree', label: 'Pedigree', icon: 'pedigree' },
            { key: 'fan', label: 'Fan Chart', icon: 'fan' },
          ].map((v) => (
            <button key={v.key} onClick={() => setDefaultTreeView(v.key)} className={`flex flex-col items-center gap-2 p-4 rounded-xl border-2 transition-all ${defaultTreeView === v.key ? 'border-indigo-500 bg-indigo-50' : 'border-gray-200 hover:border-gray-300'}`}>
              <AppIcon name={v.icon} size={24} />
              <span className="text-sm font-medium">{v.label}</span>
            </button>
          ))}
        </div>
      </section>

      <section className="bg-white rounded-xl shadow-sm border border-red-200 p-6">
        <h3 className="text-lg font-semibold text-red-700 mb-2">Reset Appearance</h3>
        <p className="text-sm text-gray-500 mb-4">Reset all appearance settings to defaults.</p>
        {showResetConfirm ? (
          <div className="bg-red-50 p-4 rounded-lg">
            <p className="text-sm text-red-700 font-medium">Reset all appearance settings?</p>
            <div className="mt-3 flex gap-3">
              <button className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 transition-colors">Yes, Reset</button>
              <button onClick={() => setShowResetConfirm(false)} className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors">Cancel</button>
            </div>
          </div>
        ) : (
          <button onClick={() => setShowResetConfirm(true)} className="px-4 py-2 bg-white border border-red-300 text-red-600 rounded-lg text-sm font-medium hover:bg-red-50 transition-colors">Reset to Defaults</button>
        )}
      </section>
    </div>
  );
}

export default SettingsPage;